import { useState } from "react";
import { useNavigate, useSearchParams, Navigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import Logo from "@/components/Logo";
import LangueSwitcher from "@/components/LangueSwitcher";

export default function Login() {
  const { t } = useTranslation();
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("mode") === "inscription" ? "register" : "login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ nom: "", email: "", password: "", raison_sociale: "", siret: "" });

  if (user) return <Navigate to="/tableau-de-bord" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register(form);
      }
      navigate("/tableau-de-bord");
    } catch (err) {
      toast.error(apiError(err, t("auth.erreur_connexion")));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#12100D] flex flex-col items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <LangueSwitcher sombre />
      </div>
      <div className="flex items-center gap-3 mb-8">
        <Logo size={48} iconSize={26} className="rounded-xl" />
        <div>
          <h1 className="font-serif font-bold text-3xl text-white">Mastoras</h1>
          <p className="text-stone-400 text-sm">{t("auth.slogan")}</p>
        </div>
      </div>

      <div className="w-full max-w-md bg-[#F7F4EE] rounded-2xl p-6 sm:p-8" data-testid="auth-panel">
        <div className="grid grid-cols-2 gap-2 mb-6 bg-white rounded-lg p-1 border border-[#E6DFD1]">
          <button onClick={() => setMode("login")} data-testid="tab-connexion"
                  className={`h-11 rounded-md font-semibold text-sm ${mode === "login" ? "bg-[#12100D] text-[#D9A461]" : "text-stone-500"}`}>
            {t("auth.connexion")}
          </button>
          <button onClick={() => setMode("register")} data-testid="tab-inscription"
                  className={`h-11 rounded-md font-semibold text-sm ${mode === "register" ? "bg-[#12100D] text-[#D9A461]" : "text-stone-500"}`}>
            {t("auth.inscription")}
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <>
              <input className="input-field" placeholder={t("auth.nom")} value={form.nom} onChange={set("nom")}
                     required data-testid="register-nom-input" />
              <input className="input-field" placeholder={t("auth.entreprise")} value={form.raison_sociale}
                     onChange={set("raison_sociale")} required data-testid="register-entreprise-input" />
              <input className="input-field" placeholder={t("auth.siret_optionnel")} value={form.siret} onChange={set("siret")}
                     data-testid="register-siret-input" />
            </>
          )}
          <input className="input-field" type="email" placeholder={t("auth.email")} value={form.email} onChange={set("email")}
                 required data-testid="login-email-input" />
          <input className="input-field" type="password" placeholder={t("auth.mdp")} value={form.password}
                 onChange={set("password")} required minLength={6} data-testid="login-password-input" />
          <button type="submit" disabled={loading} className="btn-accent w-full" data-testid="login-submit-button">
            {loading ? <Loader2 className="animate-spin" size={20} /> : mode === "login" ? t("auth.se_connecter") : t("auth.creer_compte")}
          </button>
          {mode === "register" && (
            <p className="text-xs text-stone-500 text-center" data-testid="mention-essai">
              {t("auth.essai")}
            </p>
          )}
        </form>
        {mode === "login" && (
          <p className="text-center mt-4">
            <Link to="/mot-de-passe-oublie" className="text-sm text-[#8A5A2B] font-semibold hover:underline"
                  data-testid="lien-mdp-oublie">
              {t("auth.mdp_oublie")}
            </Link>
          </p>
        )}
        {mode === "register" && (
          <p className="text-xs text-stone-400 text-center mt-4" data-testid="mention-legales-inscription">
            {t("auth.mention_1")}{" "}
            <Link to="/cgv" className="underline hover:text-stone-600">{t("auth.cgv")}</Link> {t("auth.mention_2")}{" "}
            <Link to="/confidentialite" className="underline hover:text-stone-600">{t("auth.confidentialite")}</Link>.
          </p>
        )}
      </div>
      <p className="text-stone-500 text-xs mt-6 text-center max-w-sm">
        {t("auth.rgpd")}
      </p>
    </div>
  );
}
